import { type FC } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import type { ContextBuffer } from "~/pages/manifold-nextgen/components/conversations/contextBuffer";

interface ContextBufferMeterProps {
  buffer: ContextBuffer;
  size?: number;
  strokeWidth?: number;
}

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 100_000) return `${(n / 1_000).toFixed(0)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// Warn as the context fills: neutral until 75%, amber through 90%, red beyond.
function meterColor(usagePercent: number): string {
  if (usagePercent >= 90) return "var(--color-destructive)";
  if (usagePercent >= 75) return "var(--color-orange-500)";
  return "var(--color-muted-foreground)";
}

/**
 * Circular progress meter showing context-buffer token usage. Renders an SVG
 * donut arc whose filled portion represents used tokens, with a tooltip
 * spelling out the exact counts.
 */
export const ContextBufferMeter: FC<ContextBufferMeterProps> = ({
  buffer,
  size = 18,
  strokeWidth = 3,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filledLength = (buffer.usagePercent / 100) * circumference;
  const dashOffset = circumference - filledLength;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="img"
            aria-label={`${buffer.usagePercent.toFixed(0)}% of context used`}
            tabIndex={0}
            className="flex cursor-default items-center justify-center"
            style={{ width: size, height: size }}
          >
            <svg
              width={size}
              height={size}
              viewBox={`0 0 ${size} ${size}`}
              className="rotate-[-90deg]"
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                className="opacity-15"
                strokeWidth={strokeWidth}
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={meterColor(buffer.usagePercent)}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                className="transition-all duration-300"
              />
            </svg>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p>
            {buffer.usagePercent.toFixed(1)}% · {formatTokenCount(buffer.usedTokens)} /{" "}
            {formatTokenCount(buffer.totalTokens)} context used
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
